import { useEffect, useMemo, useRef, useState } from "react";
import confetti from "canvas-confetti";
import {
  Sparkle, Rocket, Storefront, Wallet, Headset,
  ArrowRight, ArrowLeft, X, HandWaving, CheckCircle, Warning,
} from "@phosphor-icons/react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { renderRich } from "../lib/richText";

const CALENDLY_URL = "https://calendly.com/office-tee/30min";

/**
 * First-login guided wizard shown after admin activation.
 * Trigger: user.welcome_pending === true OR ?welcome=1 in URL (for re-open).
 * 4 slides: Benvenuto · Myze/Shopify · Wallet · Supporto (Calendly).
 * On completion or dismiss → POST /auth/welcome-seen (unless forced-open via URL).
 */
export default function WelcomeSplash() {
  const { user, reload } = useAuth();
  const { t } = useI18n();
  const [dismissed, setDismissed] = useState(false);
  const [slide, setSlide] = useState(0);
  const forced = useMemo(() => new URLSearchParams(window.location.search).get("welcome") === "1", []);
  const visible = (user?.welcome_pending || forced) && !dismissed;
  const rootRef = useRef(null);
  const primaryBtnRef = useRef(null);

  // Confetti burst only on slide 1
  useEffect(() => {
    if (!visible || slide !== 0) return;
    const shoot = (particleRatio, opts) => {
      confetti({
        startVelocity: 30, spread: 360, ticks: 90, zIndex: 9999,
        colors: ["#CCF22C", "#193529", "#D4A017", "#4A8A3E", "#FFFFFF"],
        ...opts,
        particleCount: Math.floor(220 * particleRatio),
      });
    };
    shoot(0.25, { spread: 26, startVelocity: 55, origin: { y: 0.65 } });
    shoot(0.2,  { spread: 60, origin: { y: 0.65 } });
    shoot(0.35, { spread: 100, decay: 0.91, scalar: 0.9, origin: { y: 0.65 } });
    shoot(0.1,  { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2, origin: { y: 0.65 } });
  }, [visible, slide]);

  // Focus trap + Esc + body scroll lock
  useEffect(() => {
    if (!visible) return;
    const focusTimer = setTimeout(() => primaryBtnRef.current?.focus(), 100);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (e) => {
      if (!rootRef.current) return;
      if (e.key === "Escape") { e.preventDefault(); dismiss(); return; }
      if (e.key !== "Tab") return;
      const focusables = rootRef.current.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusables.length === 0) return;
      const first = focusables[0], last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [visible]);

  if (!visible) return null;

  const dismiss = async () => {
    setDismissed(true);
    // rimuovi ?welcome=1 dall'url se presente
    if (forced) {
      const url = new URL(window.location.href);
      url.searchParams.delete("welcome");
      window.history.replaceState({}, "", url.pathname + url.search);
    }
    if (user?.welcome_pending) {
      try { await api.post("/auth/welcome-seen"); await reload?.(); } catch { /* silent */ }
    }
  };

  const firstName = (user?.name || "").split(" ")[0] || "";

  const slides = [
    {
      key: "welcome",
      eyebrow: <><HandWaving size={12} weight="fill" className="text-[#D4A017]" /> {t("ws_ready_eyebrow")}</>,
      icon: <div className="mx-auto w-20 h-20 rounded-full bg-[#CCF22C] flex items-center justify-center relative">
        <Rocket size={40} weight="fill" className="text-[#193529] animate-rocket-wiggle" />
        <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-[#193529] text-[#CCF22C] flex items-center justify-center">
          <Sparkle size={16} weight="fill" />
        </div>
      </div>,
      title: <>{t("ws_welcome_prefix")} <span className="whitespace-nowrap">Teefarm Connect{firstName ? "," : ""}</span>{firstName && <div className="text-[#4A8A3E] mt-1">{firstName}!</div>}</>,
      body: t("ws_welcome_body"),
      extra: (
        <div className="grid grid-cols-3 gap-2 mt-6">
          <SplashStat n="1" label={t("ws_stat_store")} />
          <SplashStat n="2" label={t("ws_stat_wallet")} />
          <SplashStat n="3" label={t("ws_stat_sell")} />
        </div>
      ),
      primaryLabel: <>{t("ws_cta_start")} <ArrowRight size={14} weight="bold" /></>,
    },
    {
      key: "store",
      eyebrow: <>{t("ws_step1_eyebrow")}</>,
      icon: <StepIcon><Storefront size={40} weight="duotone" className="text-[#193529]" /></StepIcon>,
      title: <>{t("ws_step1_title_prefix")} <span className="text-[#4A8A3E]">{t("ws_stat_store")}</span></>,
      body: <>{renderRich(t("ws_step1_body"))}</>,
      extra: <ChecklistRow items={[
        t("ws_step1_check1"),
        t("ws_step1_check2"),
        t("ws_step1_check3"),
      ]} />,
      primaryLabel: <>{t("ws_step1_cta")} <ArrowRight size={14} weight="bold" /></>,
    },
    {
      key: "wallet",
      eyebrow: <>{t("ws_step2_eyebrow")}</>,
      icon: <StepIcon><Wallet size={40} weight="duotone" className="text-[#193529]" /></StepIcon>,
      title: <>{t("ws_step2_title_prefix")} <span className="text-[#4A8A3E]">{t("ws_stat_wallet")}</span></>,
      body: <>{renderRich(t("ws_step2_body"))}</>,
      extra: <ChecklistRow items={[
        t("ws_step2_check1"),
        t("ws_step2_check2"),
        t("ws_step2_check3"),
      ]} />,
      primaryLabel: <>{t("ws_step2_cta")} <ArrowRight size={14} weight="bold" /></>,
    },
    {
      key: "support",
      eyebrow: <>{t("ws_step3_eyebrow")}</>,
      icon: <StepIcon><Headset size={40} weight="duotone" className="text-[#193529]" /></StepIcon>,
      title: <>{t("ws_step3_title_prefix")} <span className="text-[#4A8A3E]">mini-call</span> {t("ws_step3_title_suffix")}</>,
      body: <>{renderRich(t("ws_step3_body"))}</>,
      extra: (
        <div className="mt-6">
          <a
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="tf-pill-primary w-full justify-center"
            data-testid="welcome-calendly-btn"
          >
            {t("ws_step3_calendly_cta")} <ArrowRight size={14} weight="bold" />
          </a>
          <div className="mt-4 flex items-start gap-2 rounded-2xl bg-[#FFF7E5] border border-[#F2D585] p-3 text-left">
            <Warning size={16} weight="fill" className="text-[#A54B0F] mt-0.5 flex-shrink-0" />
            <div className="text-[11px] leading-relaxed text-[#5A6E63]">
              <b className="text-[#193529]">{t("ws_step3_before_call")}</b> {t("ws_step3_before_call_body")}
            </div>
          </div>
        </div>
      ),
      primaryLabel: <>{t("ws_step3_cta")} <CheckCircle size={14} weight="bold" /></>,
    },
  ];

  const total = slides.length;
  const current = slides[slide];
  const isLast = slide === total - 1;

  const next = () => {
    if (isLast) dismiss();
    else setSlide((s) => Math.min(s + 1, total - 1));
  };
  const prev = () => setSlide((s) => Math.max(s - 1, 0));

  return (
    <div
      ref={rootRef}
      className="fixed inset-0 z-[100] flex items-center justify-center px-4"
      data-testid="welcome-splash"
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-splash-title"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-[#193529]/70 backdrop-blur-sm animate-fade-in" onClick={dismiss} aria-hidden="true" />

      {/* Card */}
      <div className="relative bg-white rounded-[24px] p-8 md:p-10 max-w-lg w-full text-center border-2 border-[#CCF22C] shadow-2xl animate-splash-in">
        {/* Skip / Close */}
        <button
          onClick={dismiss}
          className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center text-[#5A6E63] hover:bg-[#F5F2E8] hover:text-[#193529] transition-colors"
          data-testid="welcome-skip-btn"
          aria-label={t("ws_skip_aria")}
        >
          <X size={16} weight="bold" />
        </button>

        {/* Icon */}
        {current.icon}

        {/* Eyebrow */}
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#5A6E63] mt-6 flex items-center justify-center gap-2">
          {current.eyebrow}
        </div>

        {/* Title */}
        <h1
          id="welcome-splash-title"
          className="font-display font-black text-3xl md:text-4xl mt-3 leading-[1.05] text-[#193529]"
          data-testid={`welcome-slide-title-${current.key}`}
        >
          {current.title}
        </h1>

        {/* Body */}
        <p className="text-sm text-[#5A6E63] mt-4 leading-relaxed">{current.body}</p>

        {/* Slide-specific extra content */}
        {current.extra}

        {/* Nav */}
        <div className="mt-8 flex items-center gap-2">
          {slide > 0 && (
            <button
              onClick={prev}
              className="inline-flex items-center justify-center h-11 w-11 rounded-full border-2 border-[#E0DFD4] text-[#193529] hover:bg-[#F5F2E8] transition-colors flex-shrink-0"
              data-testid="welcome-prev-btn"
              aria-label={t("ws_prev_aria")}
            >
              <ArrowLeft size={14} weight="bold" />
            </button>
          )}
          <button
            ref={primaryBtnRef}
            onClick={next}
            className="tf-pill-primary flex-1 justify-center"
            data-testid={isLast ? "welcome-finish-btn" : "welcome-next-btn"}
          >
            {current.primaryLabel}
          </button>
        </div>

        {/* Dots */}
        <div className="mt-5 flex items-center justify-center gap-1.5" role="tablist" aria-label={t("ws_slide_group_aria")}>
          {slides.map((s, i) => (
            <button
              key={s.key}
              onClick={() => setSlide(i)}
              className={`h-1.5 rounded-full transition-all ${i === slide ? "w-6 bg-[#193529]" : "w-1.5 bg-[#E0DFD4] hover:bg-[#C8C6BC]"}`}
              aria-label={t("ws_dot_aria", { n: i + 1 })}
              aria-current={i === slide ? "true" : undefined}
              data-testid={`welcome-dot-${i}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function SplashStat({ n, label }) {
  return (
    <div className="rounded-2xl bg-[#F5F2E8] p-3">
      <div className="font-display font-black text-lg text-[#193529] leading-none tf-numeric">{n}</div>
      <div className="text-[9px] uppercase tracking-widest text-[#5A6E63] mt-1">{label}</div>
    </div>
  );
}

function StepIcon({ children }) {
  return (
    <div className="mx-auto w-20 h-20 rounded-full bg-[#F5F2E8] flex items-center justify-center border-2 border-[#CCF22C]">
      {children}
    </div>
  );
}

function ChecklistRow({ items }) {
  return (
    <ul className="mt-6 space-y-2 text-left">
      {items.map((t, i) => (
        <li key={i} className="flex items-start gap-2 text-xs text-[#5A6E63]">
          <CheckCircle size={16} weight="fill" className="text-[#4A8A3E] flex-shrink-0 mt-0.5" />
          <span>{t}</span>
        </li>
      ))}
    </ul>
  );
}
